import React, { useState, useMemo, useEffect, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import debounce from "lodash.debounce";
import { useGlobalStyles } from "./globalStyles";
import { commonStrings } from "./StringBundle";

const SelectMultipleCreate = ({
  name,
  control,
  label,
  required = false,
  options = [],
  fetchOptions,
  labelKey = "label",
  columns = [],
  disabled = false,
  isTableView = false,
  placeholder,
  helperText,
  multiple = false,
  dependencies = [],
}) => {
  const classes = useGlobalStyles();
  const { control: contextControl } = useFormContext();
  const dependencyValues = useWatch({
    control: control || contextControl,
    name: dependencies,
  });

  const [loadedOptions, setLoadedOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const isMobile = window.innerWidth < 600;
  const labelWithAsterisk = required ? `${label} *` : label;

  useEffect(() => {
    if (dependencies && dependencies.length > 0) {
      setLoadedOptions(null);
      setInputValue("");
    }
  }, [dependencyValues]);

  useEffect(() => {
    setLoadedOptions(null);
    setInputValue("");
  }, [fetchOptions]);

  const fetchData = async (query = "") => {
    if (!fetchOptions) return options;

    setLoading(true);
    try {
      const res = await fetchOptions(query);
      const result = res || [];
      setLoadedOptions(result);
      return result;
    } catch (err) {
      console.error(err);
      setLoadedOptions([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Debounced fetch for search-as-you-type
  const debouncedFetch = useMemo(
    () => debounce(fetchData, 500),
    [fetchOptions, dependencyValues]
  );

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  async function handleOpen() {
    if (!fetchOptions) {
      setLoadedOptions(options);
      return;
    }
    if (loadedOptions !== null) return;
    await fetchData(inputValue || "");
  }

  function getOptionLabel(option) {
    if (!option) return "";
    if (labelKey) return option[labelKey]?.toString() || "";
    return option.label ? option.label : "";
  }

  function getRenderOption(props, option, opts) {
    const isHeader = isTableView && opts?.length && opts[0] === option;
    if (isMobile && isHeader) return null;
    return (
      <li
        {...props}
        className={isHeader ? props.className : `${props.className} ${classes.tableDataRow}`}
        style={{
          fontWeight: isHeader ? "bold" : "normal",
          opacity: 1,
          backgroundColor: isHeader ? "#feecda" : undefined,
          ...(isHeader && { position: "sticky", top: 0, zIndex: 1 }),
        }}
      >
        {!isMobile ? (
          <div className={classes.dfWFull}>
            {opts?.length > 1 ? (
              <>
                {columns.map((col) => (
                  <span key={col.key} className={classes.tableShrink}>
                    {option[col.key]}
                  </span>
                ))}
              </>
            ) : (
              <div className={classes.alignCenter}>{commonStrings.noOptions}</div>
            )}
          </div>
        ) : (
          <div className={classes.dropDownCardMain}>
            {opts?.length > 1 ? (
              <>
                {columns.map((col) => (
                  <div key={col.key} className={classes.dropDownCard}>
                    <strong>{col.label}:</strong> {option[col.key]}
                  </div>
                ))}
              </>
            ) : (
              <div className={classes.alignCenter}>{commonStrings.noOptions}</div>
            )}
          </div>
        )}
      </li>
    );
  }

  const resolvedOptions = isTableView ? (loadedOptions || []) : options;

  const filterOptionsHandler = fetchOptions
    ? (opts) => opts
    : (opts, { inputValue: searchInput }) => {
      if (!searchInput) return opts;
      const [header, ...rest] = opts;
      const lower = searchInput.toLowerCase();
      const filtered = [];
      for (const opt of rest) {
        if (String(getOptionLabel(opt)).toLowerCase().includes(lower)) {
          filtered.push(opt);
        }
      }
      return [header, ...filtered];
    };

  const inputChangeHandler = fetchOptions
    ? (_, value, reason) => {
      if (reason === "input") {
        setInputValue(value);
        const trimmed = value.trim();
        const prevTrimmed = inputValue.trim();
        if (trimmed.length >= 3) {
          debouncedFetch(value);
        } else if (trimmed.length < 3 && prevTrimmed.length >= 3) {
          debouncedFetch("");
        } else if (trimmed.length === 0 && prevTrimmed !== trimmed && prevTrimmed.length < 3) {
          debouncedFetch("");
        }
      } else if (reason === "clear") {
        setInputValue("");
        debouncedFetch("");
      }
    }
    : undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          multiple={multiple}
          fullWidth
          disabled={disabled}
          clearOnBlur={false}
          loading={loading}
          options={resolvedOptions}
          value={multiple ? (Array.isArray(field.value) ? field.value : []) : (field.value ?? null)}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(option, val) => getOptionLabel(option) === getOptionLabel(val)}
          renderOption={
            isTableView
              ? (props, option) => getRenderOption(props, option, resolvedOptions)
              : undefined
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={labelWithAsterisk}
              placeholder={placeholder}
              helperText={error?.message || helperText}
              error={!!error}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          onChange={(_, newValue) => field.onChange(newValue)}
          getOptionDisabled={(option) =>
            isTableView && resolvedOptions[0] === option
          }
          filterOptions={filterOptionsHandler}
          onInputChange={inputChangeHandler}
          onOpen={handleOpen}
        />
      )}
    />
  );
};

export default SelectMultipleCreate;
